import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import TwoFactorVerification from './TwoFactorAuth/TwoFactorVerification';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Container,
  Grid,
  InputAdornment,
  IconButton,
  Fade,
  Grow,
  Link,
} from '@mui/material';
import {
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Science as ScienceIcon,
  TrendingUp as TrendingUpIcon,
  Grass as GrassIcon,
  Analytics as AnalyticsIcon,
} from '@mui/icons-material';

import { useColorScheme } from '@mui/material/styles';
import { useAuth } from '../hooks/useAuth';
import { DRIS_SHADOWS, DRIS_TRANSITIONS } from '../theme/drisTheme';
import ResendVerification from './AgentPortal/ResendVerification';

const LoginPage: React.FC = () => {
  const { mode, systemMode } = useColorScheme();
  const resolvedMode = mode === 'system' ? systemMode : mode;
  const navigate = useNavigate();
  const { login, verify2FA, isLoading, error } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [show2FADialog, setShow2FADialog] = useState(false);
  const [verificationError, setVerificationError] = useState<string | null>(null);

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [field]: event.target.value }));
    if (errors[field as keyof typeof errors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = () => {
    const newErrors: { email?: string; password?: string } = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (validateForm()) {
      try {
        const result = await login(formData);
        if (result && 'requires2FA' in result && result.requires2FA) {
          setShow2FADialog(true);
        }
      } catch {
        // Error is handled by the context
      }
    }
  };

  const handle2FAVerify = async (code: string) => {
    try {
      setVerificationError(null);
      await verify2FA(code);
      setShow2FADialog(false);
    } catch (error) {
      setVerificationError(error instanceof Error ? error.message : 'Verification failed');
      throw error; // Re-throw so the component can handle it
    }
  };

  const handle2FACancel = () => {
    setShow2FADialog(false);
    setFormData({ email: '', password: '' });
    setVerificationError(null);
  };

  const features = [
    { icon: <ScienceIcon />, title: 'Accurate Diagnosis', description: 'AI-powered analysis' },
    { icon: <TrendingUpIcon />, title: 'Data-Driven', description: 'Smart recommendations' },
    { icon: <GrassIcon />, title: 'Sustainable', description: 'Eco-friendly solutions' },
    { icon: <AnalyticsIcon />, title: 'Real-time Monitoring', description: '24/7 crop tracking' }
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: { xs: 2, md: 4 },
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Background Pattern */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.03,
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%232E7D32' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          zIndex: 0
        }}
      />
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Fade in timeout={800}>
          <Grid container spacing={6} alignItems="center">
            {/* Left side - Hero Content */}
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Box sx={{ color: 'primary.main', mb: 4 }}>
                {/* Logo Section */}
                <Grow in timeout={1000}>
                  <Box sx={{
                    display: 'flex',
                    justifyContent: { xs: 'center', md: 'flex-start' },
                    mb: 4
                  }}>
                    <img
                      src={resolvedMode === 'dark' ? '/DRIS-WT-LS.png' : '/Artboard 1.png'}
                      alt="DRIS Logo"
                      style={{
                        height: '120px',
                        width: 'auto',
                        maxWidth: '100%',
                        objectFit: 'contain',
                        filter: 'drop-shadow(0 6px 20px rgba(46, 125, 50, 0.15))'
                      }}
                    />
                  </Box>
                </Grow>

                {/* Tagline */}
                <Grow in timeout={1200}>
                  <Box sx={{ textAlign: { xs: 'center', md: 'left' }, mb: 3 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 700,
                        color: 'primary.main',
                        mb: 1,
                        fontSize: { xs: '1.5rem', md: '2rem' }
                      }}
                    >
                      Advanced Crop Nutrition Intelligence
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 400,
                        color: 'primary.light',
                        fontSize: { xs: '1rem', md: '1.125rem' }
                      }}
                    >
                      Precision Agriculture Made Simple
                    </Typography>
                  </Box>
                </Grow>

                <Typography
                  variant="body1"
                  sx={{
                    mb: 4,
                    color: 'text.secondary',
                    fontSize: '1.125rem',
                    lineHeight: 1.7
                  }}
                >
                  Empowering farmers with precision agriculture through data-driven insights
                  and real-time monitoring for optimal crop health and yield.
                </Typography>

                {/* Feature Cards */}
                <Grid container spacing={2}>
                  {features.map((feature, index) => (
                    <Grid key={index} size={6}>
                      <Fade in timeout={1200 + index * 200}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            bgcolor: 'background.paper',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                            transition: DRIS_TRANSITIONS.smooth,
                            cursor: 'pointer',
                            '&:hover': {
                              borderColor: 'primary.light',
                            }
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Box sx={{ color: 'primary.light', mr: 1.5 }}>
                              {feature.icon}
                            </Box>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {feature.title}
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {feature.description}
                          </Typography>
                        </Paper>
                      </Fade>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Grid>

            {/* Right side - Login Form */}
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Grow in timeout={1000}>
                <Paper
                  elevation={0}
                  sx={{
                    padding: { xs: 3, md: 5 },
                    borderRadius: 3,
                    bgcolor: 'background.paper',
                    boxShadow: DRIS_SHADOWS.xlarge,
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: DRIS_TRANSITIONS.smooth,
                    '&:hover': {
                      boxShadow: '0 30px 60px rgba(0, 0, 0, 0.15)',
                    }
                  }}
                >
                  {/* Login Header */}
                  <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 600,
                        color: 'primary.main',
                        mb: 1
                      }}
                    >
                      Welcome Back
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Sign in to access your DRIS dashboard
                    </Typography>
                  </Box>

                  {/* Login Form */}
                  <form onSubmit={handleSubmit}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      variant="outlined"
                      value={formData.email}
                      onChange={handleChange('email')}
                      error={!!errors.email}
                      helperText={errors.email}
                      sx={{
                        mb: 3,
                        '& .MuiInputLabel-root': { color: 'text.primary' },
                        '& .MuiOutlinedInput-root': {
                          transition: DRIS_TRANSITIONS.smooth,
                        }
                      }}
                      disabled={isLoading}
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <EmailIcon sx={{ color: 'text.secondary' }} />
                            </InputAdornment>
                          ),
                        }
                      }}
                    />

                    <TextField
                      fullWidth
                      label="Password"
                      type={showPassword ? 'text' : 'password'}
                      variant="outlined"
                      value={formData.password}
                      onChange={handleChange('password')}
                      error={!!errors.password}
                      helperText={errors.password}
                      sx={{
                        mb: 4,
                        '& .MuiInputLabel-root': { color: 'text.primary' },
                        '& .MuiOutlinedInput-root': {
                          transition: DRIS_TRANSITIONS.smooth,
                        }
                      }}
                      disabled={isLoading}
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <LockIcon sx={{ color: 'text.secondary' }} />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPassword(!showPassword)}
                                edge="end"
                                sx={{ color: 'text.secondary' }}
                              >
                                {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }
                      }}
                    />

                    {error && (
                      <Fade in>
                        <Box
                          sx={{
                            mb: 3,
                            p: 2,
                            bgcolor: 'error.main',
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'error.light',
                            // Light error background using alpha
                            backgroundColor: (theme) => `rgba(${theme.vars.palette.error.mainChannel} / 0.08)`,
                          }}
                        >
                          <Typography color="error" variant="body2">
                            {error}
                          </Typography>
                          {/* Unverified accounts are indistinguishable from wrong credentials by
                              design (login filters on IsActive), so this hint is STATIC: it renders
                              on every failure, identically, regardless of the address typed. Making
                              it conditional on the account's state would be an enumeration oracle. */}
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Just registered? You must verify your email before you can sign in.
                          </Typography>
                          <ResendVerification
                            defaultEmail={formData.email}
                            linkLabel="Resend verification email"
                          />
                        </Box>
                      </Fade>
                    )}

                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      size="large"
                      disabled={isLoading}
                      sx={{
                        mb: 3,
                        py: 1.75,
                        fontSize: '1.1rem',
                        fontWeight: 500,
                        bgcolor: 'primary.main',
                        color: '#fff',
                        boxShadow: DRIS_SHADOWS.medium,
                        transition: DRIS_TRANSITIONS.smooth,
                        '&:hover': {
                          bgcolor: 'primary.dark',
                          color: '#fff',
                        },
                      }}
                    >
                      {isLoading ? 'Signing in...' : 'Sign In'}
                    </Button>

                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                      <Link
                        component="button"
                        type="button"
                        onClick={() => navigate('/forgot-password')}
                        sx={{
                          color: 'primary.main',
                          textDecoration: 'none',
                          fontSize: '0.875rem',
                          transition: DRIS_TRANSITIONS.fast,
                          '&:hover': {
                            textDecoration: 'underline',
                          }
                        }}
                      >
                        Forgot your password?
                      </Link>
                    </Box>


                    {/* Agent Portal registration entry point */}
                    <Box sx={{ textAlign: 'center', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        New to the Agent Portal?{' '}
                        <Link
                          component="button"
                          type="button"
                          onClick={() => navigate('/register')}
                          sx={{ color: 'primary.main', fontWeight: 500 }}
                        >
                          Create an account
                        </Link>
                      </Typography>
                    </Box>

                    {/* Support Info */}
                    <Box sx={{ mt: 3, textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary">
                        Need help? Contact support at info@dris.ag
                      </Typography>
                    </Box>
                  </form>
                </Paper>
              </Grow>
            </Grid>
          </Grid>
        </Fade>
      </Container>
      {/* 2FA Verification Dialog */}
      <TwoFactorVerification
        open={show2FADialog}
        onVerify={handle2FAVerify}
        onCancel={handle2FACancel}
        isLoading={isLoading}
        error={verificationError}
      />
    </Box>
  );
};

export default LoginPage;
